/**
 * Рендер DOM-узла в PDF «как при печати» — с фонами, рамками, цветами
 * и шрифтами, то есть полноценная копия печатной формы, а не список строк.
 *
 * Почему canvas, а не html2canvas/jsPDF: в проекте нет этих библиотек, а
 * PyPI/npm-доступ на сервере не нужен — всё считается в браузере.
 *
 * Как работает:
 *   1. обходим дерево и собираем команды рисования:
 *      фоны/рамки элементов, слова (через Range.getClientRects — они дают
 *      точные координаты каждой строки с учётом переносов, выравнивания и
 *      межбуквенного интервала), картинки и inline-SVG;
 *   2. режем контент на страницы A4, не разрывая строки текста;
 *   3. каждую страницу рисуем на canvas и упаковываем в PDF (/DCTDecode).
 *
 * Текст в итоге растровый (не выделяется мышью), но выглядит как печатный
 * документ — для отправки пациенту это то, что нужно.
 */
import { packPdf } from "./pdfDocument";

const A4_W = 794; // A4 при 96 dpi
const A4_H = 1123;

const SKIP_CLASS = "no-print";
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "LINK", "META"]);

const toBase64Binary = (dataUrl) => {
  const base = String(dataUrl).split(",")[1] || "";
  const binary = atob(base);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const parseColor = (value) => {
  const text = String(value || "").trim();
  if (!text || text === "transparent") return null;
  if (text.startsWith("#")) {
    const hex = text.slice(1);
    if (hex.length === 3) {
      return [
        parseInt(hex[0] + hex[0], 16),
        parseInt(hex[1] + hex[1], 16),
        parseInt(hex[2] + hex[2], 16),
        1,
      ];
    }
    if (hex.length === 6 || hex.length === 8) {
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
        hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
      ];
    }
    return null;
  }
  const match = text.match(
    /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?\s*\)/i
  );
  if (!match) return null;
  return [
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    match[4] === undefined ? 1 : Number(match[4]),
  ];
};

const cssColor = (rgba) =>
  rgba
    ? `rgba(${Math.round(rgba[0])},${Math.round(rgba[1])},${Math.round(rgba[2])},${rgba[3]})`
    : "rgba(0,0,0,1)";

const applyTransform = (text, transform) => {
  const mode = String(transform || "none").toLowerCase();
  if (mode === "uppercase") return text.toUpperCase();
  if (mode === "lowercase") return text.toLowerCase();
  if (mode === "capitalize") return text.replace(/\b\p{L}/gu, (c) => c.toUpperCase());
  return text;
};

/** Угол/цвета простого linear-gradient из computed background-image. */
const parseGradient = (value) => {
  const text = String(value || "");
  if (!text.includes("gradient")) return null;
  const angleMatch = text.match(/([\d.]+)deg/);
  const colors = text.match(/rgba?\([^)]*\)|#[0-9a-fA-F]{3,8}/g);
  if (!colors || colors.length < 2) return null;
  const stops = colors.map(parseColor).filter(Boolean);
  if (stops.length < 2) return null;
  return { angle: angleMatch ? Number(angleMatch[1]) : 180, stops };
};

const radiusOf = (cs) => {
  const raw = cs.borderTopLeftRadius || "0";
  return Math.max(0, parseFloat(raw) || 0);
};

const roundRectPath = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, w / 2, h / 2);
  if (!radius) {
    ctx.rect(x, y, w, h);
    return;
  }
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

/** Превращает inline-SVG в загруженный Image (чтобы нарисовать на canvas). */
const rasterizeSvg = (svg, view) =>
  new Promise((resolve) => {
    try {
      const clone = svg.cloneNode(true);
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      const source = Array.from(svg.querySelectorAll("*")).concat([svg]);
      const clones = Array.from(clone.querySelectorAll("*")).concat([clone]);
      source.forEach((node, index) => {
        const cs = view.getComputedStyle(node);
        const target = clones[index];
        if (!target || !target.setAttribute) return;
        if (cs.fill && cs.fill !== "none") target.setAttribute("fill", cs.fill);
        if (cs.stroke && cs.stroke !== "none") {
          target.setAttribute("stroke", cs.stroke);
          target.setAttribute("stroke-width", cs.strokeWidth || "1");
        }
      });
      const markup = new XMLSerializer().serializeToString(clone);
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = url;
    } catch (_) {
      resolve(null);
    }
  });

/** Собирает команды рисования из DOM. */
async function collect(root, view) {
  const origin = root.getBoundingClientRect();
  const boxes = [];
  const texts = [];
  const images = [];
  const doc = view.document;
  const range = doc.createRange();

  const collectText = (node, cs) => {
    const data = node.nodeValue || "";
    if (!data.trim()) return;
    const re = /\S+/g;
    let match = re.exec(data);
    while (match) {
      range.setStart(node, match.index);
      range.setEnd(node, match.index + match[0].length);
      const rects = range.getClientRects();
      for (let i = 0; i < rects.length; i += 1) {
        const rect = rects[i];
        if (rect.width < 0.4 || rect.height < 0.4) continue;
        texts.push({
          text: applyTransform(match[0], cs.textTransform),
          x: rect.left - origin.left,
          y: rect.top - origin.top,
          w: rect.width,
          h: rect.height,
          font: `${cs.fontStyle === "italic" ? "italic " : ""}${
            cs.fontWeight || "400"
          } ${parseFloat(cs.fontSize) || 12}px ${cs.fontFamily || "sans-serif"}`,
          size: parseFloat(cs.fontSize) || 12,
          color: cssColor(parseColor(cs.color)),
          letterSpacing: parseFloat(cs.letterSpacing) || 0,
        });
      }
      match = re.exec(data);
    }
  };

  const svgTasks = [];

  const walk = (el) => {
    if (!el || el.nodeType !== 1) return;
    const tag = el.tagName;
    if (SKIP_TAGS.has(tag)) return;
    if (el.classList && el.classList.contains(SKIP_CLASS)) return;

    const cs = view.getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden") return;
    if (parseFloat(cs.opacity) === 0) return;

    const rect = el.getBoundingClientRect();
    const x = rect.left - origin.left;
    const y = rect.top - origin.top;
    const w = rect.width;
    const h = rect.height;

    if (w > 0.5 && h > 0.5) {
      const bg = parseColor(cs.backgroundColor);
      const gradient = parseGradient(cs.backgroundImage);
      const borders = [
        { side: "top", width: parseFloat(cs.borderTopWidth) || 0, color: parseColor(cs.borderTopColor), style: cs.borderTopStyle },
        { side: "right", width: parseFloat(cs.borderRightWidth) || 0, color: parseColor(cs.borderRightColor), style: cs.borderRightStyle },
        { side: "bottom", width: parseFloat(cs.borderBottomWidth) || 0, color: parseColor(cs.borderBottomColor), style: cs.borderBottomStyle },
        { side: "left", width: parseFloat(cs.borderLeftWidth) || 0, color: parseColor(cs.borderLeftColor), style: cs.borderLeftStyle },
      ].filter((b) => b.width > 0.3 && b.color && b.style !== "none" && b.style !== "hidden");

      if ((bg && bg[3] > 0) || gradient || borders.length) {
        boxes.push({ x, y, w, h, radius: radiusOf(cs), bg, gradient, borders });
      }
    }

    if (tag === "IMG") {
      if (w > 0.5 && h > 0.5 && el.complete && el.naturalWidth) {
        images.push({ el, x, y, w, h });
      }
      return;
    }

    if (tag === "svg" || tag === "SVG") {
      if (w > 0.5 && h > 0.5) svgTasks.push({ el, x, y, w, h });
      return;
    }

    for (const child of Array.from(el.childNodes)) {
      if (child.nodeType === 3) collectText(child, cs);
      else if (child.nodeType === 1) walk(child);
    }
  };

  walk(root);

  const svgs = await Promise.all(
    svgTasks.map(async (task) => ({ ...task, image: await rasterizeSvg(task.el, view) }))
  );

  return { boxes, texts, images, svgs };
}

/** Точки разрыва страниц — по верхней границе строки, которую нельзя рвать. */
function computeBreaks(texts, totalHeight, pageHeight) {
  const breaks = [0];
  let top = 0;
  const guard = 400; // максимум страниц
  for (let index = 0; index < guard; index += 1) {
    if (top + pageHeight >= totalHeight) break;
    let next = top + pageHeight;
    for (const line of texts) {
      if (line.y > top + 40 && line.y < next && line.y + line.h > next) {
        next = line.y;
      }
    }
    if (next <= top + 80) next = top + pageHeight;
    breaks.push(next);
    top = next;
  }
  return breaks;
}

function drawPage(commands, pageTop, pageHeight, scale, fit) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(A4_W * scale);
  canvas.height = Math.round(A4_H * scale);
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  // Содержимое печатной формы шире A4 — вписываем по ширине страницы.
  ctx.scale(scale * fit, scale * fit);
  ctx.translate(0, -pageTop);
  ctx.textBaseline = "middle";

  const inPage = (y, h) => y + h > pageTop - 2 && y < pageTop + pageHeight + 2;

  // 1. Фоны и рамки.
  for (const box of commands.boxes) {
    if (!inPage(box.y, box.h)) continue;
    ctx.save();
    ctx.beginPath();
    roundRectPath(ctx, box.x, box.y, box.w, box.h, box.radius);
    if (box.gradient) {
      const { angle, stops } = box.gradient;
      const rad = ((angle - 90) * Math.PI) / 180;
      const cx = box.x + box.w / 2;
      const cy = box.y + box.h / 2;
      const len = Math.max(box.w, box.h) / 2;
      const grad = ctx.createLinearGradient(
        cx - Math.cos(rad) * len,
        cy - Math.sin(rad) * len,
        cx + Math.cos(rad) * len,
        cy + Math.sin(rad) * len
      );
      stops.forEach((stop, index) => {
        const offset = stops.length === 1 ? 0 : index / (stops.length - 1);
        grad.addColorStop(offset, cssColor(stop));
      });
      ctx.fillStyle = grad;
      ctx.fill();
    } else if (box.bg && box.bg[3] > 0) {
      ctx.fillStyle = cssColor(box.bg);
      ctx.fill();
    }
    ctx.restore();

    for (const border of box.borders) {
      ctx.fillStyle = cssColor(border.color);
      if (border.side === "top") ctx.fillRect(box.x, box.y, box.w, border.width);
      else if (border.side === "bottom") {
        ctx.fillRect(box.x, box.y + box.h - border.width, box.w, border.width);
      } else if (border.side === "left") ctx.fillRect(box.x, box.y, border.width, box.h);
      else if (border.side === "right") {
        ctx.fillRect(box.x + box.w - border.width, box.y, border.width, box.h);
      }
    }
  }

  // 2. Картинки и иконки.
  for (const image of commands.images) {
    if (!inPage(image.y, image.h)) continue;
    try {
      ctx.drawImage(image.el, image.x, image.y, image.w, image.h);
    } catch (_) {
      /* картинка может быть недоступна — не критично */
    }
  }
  for (const svg of commands.svgs) {
    if (!svg.image || !inPage(svg.y, svg.h)) continue;
    try {
      ctx.drawImage(svg.image, svg.x, svg.y, svg.w, svg.h);
    } catch (_) {
      /* пропускаем */
    }
  }

  // 3. Текст.
  for (const line of commands.texts) {
    if (!inPage(line.y, line.h)) continue;
    ctx.font = line.font;
    ctx.fillStyle = line.color;
    if ("letterSpacing" in ctx && line.letterSpacing) {
      ctx.letterSpacing = `${line.letterSpacing}px`;
    }
    ctx.fillText(line.text, line.x, line.y + line.h / 2 + line.size * 0.02);
  }
  if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";

  return canvas;
}

/**
 * Рендерит узел в PDF.
 * @param {Element} root — корень печатной формы
 * @param {{title?: string, scale?: number}} options
 * @returns {Promise<Blob>}
 */
export async function renderNodeToPdf(root, options = {}) {
  const { title = "Документ", scale = 2 } = options;
  if (!root) throw new Error("Не передан узел печатной формы");

  const view = root.ownerDocument?.defaultView;
  if (!view) throw new Error("Не найден документ узла");

  // Шрифты должны быть загружены, иначе текст съедет по метрикам.
  if (view.document.fonts?.ready) {
    try {
      await view.document.fonts.ready;
    } catch {
      /* не критично */
    }
  }

  const commands = await collect(root, view);
  const rootRect = root.getBoundingClientRect();
  const contentWidth = Math.max(rootRect.width, 320);
  // Вписываем форму по ширине A4: на экране лист может быть шире страницы.
  const fit = A4_W / contentWidth;
  const pageHeightPx = A4_H / fit;
  const rootHeight = Math.max(
    rootRect.height,
    ...commands.texts.map((line) => line.y + line.h).concat([0])
  );
  const breaks = computeBreaks(commands.texts, rootHeight + 24, pageHeightPx);

  // Каждую страницу сразу кодируем в JPEG и отпускаем canvas — иначе на
  // длинном плане (5+ страниц) в памяти висит несколько десятков мегабайт.
  const jpegs = [];
  for (const top of breaks) {
    const canvas = drawPage(commands, top, pageHeightPx, scale, fit);
    jpegs.push(toBase64Binary(canvas.toDataURL("image/jpeg", 0.92)));
    canvas.width = 1;
    canvas.height = 1;
  }

  return packPdf(
    jpegs,
    Math.round(A4_W * scale),
    Math.round(A4_H * scale),
    title
  );
}

export default renderNodeToPdf;
